import React, { useState, useEffect } from 'react';
import Has from '../../component/Permission/Has';
import { ROLE_INTERNAL_U_USER_ADMIN } from '../../config/PermissionTypes';
import { useNavigate } from "react-router-dom";
import { useCompany, useCompanyPermission } from '../../component/CompanySelection/CompanyContext';
import { getInternalRoles } from '../../service/UserService';
import LoadingSpinner from '../../component/LoadingSpinner/LoadingSpinner';
import { ROLE_NAME_REGEX } from '../../validators/InputValidator';
import { getUrl } from '../../util/EfupUtility';
import { toDateString } from '../../util/DateUtility';
import { formatTestId } from '../../util/EfupUtility';
import { Link } from "react-router-dom";
import SidePanel from '../../component/SidePanel/SidePanel';
import { INTERNAL_ROLE_SIDE_PANEL } from '../../config/SidePanelTypes';
import './InternalRoles.css'



export default function InternalRoles(props) {
    const [allRoles, setAllRoles] = useState([]);   //this will be master list that can be filtered & searched from
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [role, setRole] = useState("");           // current role being viewed
    const [sortColumn, setSortColumn] = useState("roleName");
    const [sortDirection, setSortDirection] = useState("desc");
    const [searchValue, setSearchValue] = useState("");
    const navigate = useNavigate();

    let isEditClicked = false;
    function onRowSelect(role) {
        if (isEditClicked === true) {
            isEditClicked = false;
        } else {
            setRole(role);
            setIsModalOpen(true);
        }
    }

    function onEditLink() {
        isEditClicked = true;
    }

    const handleSearchChange = (value) => {
        const regex = new RegExp(ROLE_NAME_REGEX);
        if (regex.test(value)) {
            setSearchValue(value);
            applyFilters(value);
        }
    }

    const applyFilters = (searchValue) => {
        let roles = allRoles;
        if (searchValue !== "") {
            searchValue = searchValue.toLowerCase();
            roles = roles.filter(r => r.roleName.toLowerCase().includes(searchValue));
        }
        setRoles(roles);
    }

    //this sets the sort icon of the currently sorted column
    const iconClass = (column) => {
        const dir = sortColumn === column ? `_${sortDirection}ending` : '';
        return "cb-icon icon-sort" + dir;
    }

    const setSort = (column) => {
        let direction = 'desc';
        if (column) {
            if (column !== sortColumn) {
                setSortColumn(column);
                setSortDirection(direction);
            } else {
                direction = sortDirection === 'desc' ? 'asc' : 'desc';
                setSortDirection(direction)
            }
        }
        const val = direction === "asc" ? 1 : -1;
        roles.sort((a, b) => (b[column].toString().localeCompare(a[column].toString())) * val);
    }

    const errorHandler = (error) => {
        if (error?.response?.status !== 200) {
            setHasError(true);
            setAllRoles([]);
            setRoles([]);
        } else {
            setHasError(false);
        }
        setIsLoading(false);
    }
    
    const dataHandler = (internalRoles) => {
        setIsLoading(false);
        //initially sort roles by role roleName
        const roles = internalRoles.sort((a, b) => a.roleName.localeCompare(b.roleName));
        setAllRoles(roles);
        setRoles(roles);
    }

    useEffect(() => {
        getInternalRoles(dataHandler, errorHandler);
    }, []);

    //if permissions are not loaded, show loading spinner
    const companyPermission = useCompanyPermission();
    if (!companyPermission) {
        return (
            <div id="dashboard-loader">
                <LoadingSpinner blue />
            </div>
        );
    }

    const columnHeaders = [
        { display: "Role Name", value: "roleName", sortable: true },
        { display: "Description", value: "description", sortable: false },
        { display: "Created Date", value: "createdDate", sortable: true }
    ];


    return (
        <><Has isPageLevel={true} permission={ROLE_INTERNAL_U_USER_ADMIN}>
            <div className="container-fluid internal-roles-page" data-testid="internal-roles-page" id="internal-roles-page">
                <div className="cb-row page-title-area">
                    <div className="cb-col-xs-8 cb-col-lg-8 page-title">
                        <h1 data-testid="add-user-page-header-title">Internal Roles</h1>
                    </div>
                </div>

                <div className="row roles-table-runner">
                    <div className="col-md-6 role-filters">
                        <div>
                            <div className="sectionTitle" data-testid="search-roles-label">Search Roles</div>
                            <div className="role-search-container">
                                <input className='cb-form-control'
                                    type="text"
                                    data-testid="role-search-input"
                                    maxLength="256"
                                    onChange={e => handleSearchChange(e.target.value)}
                                    placeholder="Role Name"
                                    value={searchValue}
                                />
                                <i className="cb-icon icon-search"></i>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <Has permission={ROLE_INTERNAL_U_USER_ADMIN}>
                            <div className="add-role-button-container">
                                <button
                                    type="button"
                                    className="cb-btn add-role-button d-lg-block"
                                    data-testid="add-role-button"
                                    onClick={() => navigate("/system/internalRoles/add")}>
                                    + Add New Role
                                </button>
                            </div>
                        </Has>
                    </div>
                </div>

                {isLoading ? (
                    <div className="roles-message" data-testid="roles-loading">
                        <LoadingSpinner blue size={50} />
                        <p className='text-center'>Loading internal role data...</p>
                    </div>
                ) : null}
                {!isLoading && hasError ? (
                    <div className="roles-message">
                        <h3 className="text-center" data-testid="roles-data-error">An error occurred getting internal role data.</h3>
                    </div>
                ) : null}
                {!isLoading && !hasError && !roles.length ? (
                    <div className="roles-message">
                        <h3 className="text-center" data-testid="roles-data-no-results">No internal roles found.</h3>
                    </div>
                ) : null}

                {!isLoading && !hasError && roles.length ? (
                    <div className="cb-table-responsive roles-table" data-testid="roles-table">
                        <table className="table table-sort table-arrows cb-table">
                            <thead className="table-light thead">
                                <tr>
                                    {columnHeaders.map(item => {
                                        if (item.sortable) {
                                            return <th tabIndex={0} onKeyDown={({ key }) => key === "Enter" ? setSort(item.value) : ""}>
                                                <span onClick={() => setSort(item.value)} id={`roles-table-${item.value}-header`} data-testid={`roles-table-${item.value}-header`} className="sortable">
                                                    {item.display} <i className={iconClass(item.value)}></i>
                                                </span>
                                            </th>
                                        } else {
                                            return <th><span data-testid={`roles-table-${item.value}-header`}>
                                                {item.display}
                                            </span></th>
                                        }
                                    })}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map((role, index) => {
                                    const editLinkTestId = formatTestId(`edit-role-link-${role.roleName}`);
                                    return (
                                        <tr key={`role-row-${index}`} onClick={() => onRowSelect(role)} tabIndex={0} onKeyDown={({ key }) => key === "Enter" ? onRowSelect(role) : ""}>
                                            <td>{role.roleName}</td>
                                            <td>{role.description}</td>
                                            <td>{toDateString(role.createdDate)}</td>
                                            <td className="text-right">
                                                {!role.standardRole &&
                                                    <Has permission={ROLE_INTERNAL_U_USER_ADMIN}>
                                                        <Link onKeyDown={({ key }) => key === "Enter" ? onEditLink() : ""}
                                                            className="edit-cell" data-testid={editLinkTestId}
                                                            to={'/system/internalRoles/' + role.roleUuid + '/edit'}
                                                            state={{editRole: role}}
                                                        >
                                                            <i className="cb-icon icon-edit" />
                                                            <span>Edit</span>
                                                        </Link>
                                                    </Has>
                                                }
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                        <SidePanel
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                sidePanelType={INTERNAL_ROLE_SIDE_PANEL}
                                role={role} />
                    </div>
                ) : null}


            </div>
        </Has></>
    );
}